// ---------------------------------------------------------------------------
// events.bindings key format — the single canonical `${elementId}:${eventType}`
// contract shared by every builder (studio/page-editor export, headless
// ingestion, validator fixers, abox render-prep) AND the runtime parser
// (app-runtime.ts registerBlockBindings, use-block-host). Centralizing it here
// guarantees build/parse symmetry: a binding written with `bindingKey` is read
// back identically with `parseBindingKey`.
// ---------------------------------------------------------------------------

import type { EventBinding } from "../schema/events";

/**
 * Canonical join of an element id and an event name into an `events.bindings`
 * map key. Single literal colon, no escaping — preserves the EXACT format every
 * existing build site emits (`${el.id}:${eventType}`).
 */
export function bindingKey(elementId: string, eventType: string): string {
  return `${elementId}:${eventType}`;
}

/**
 * Inverse of {@link bindingKey}. Splits on the LAST colon so an element id that
 * itself contains a colon keeps everything before the final separator — this
 * matches the authoritative runtime reader (app-runtime.ts registerBlockBindings)
 * and the studio host reader (use-block-host.ts), both of which use
 * `lastIndexOf(":")`.
 *
 * Returns `null` for keys with no usable colon (`colonIdx <= 0`, i.e. a leading
 * or absent colon), mirroring those readers' skip guard — callers should treat
 * `null` as "not a binding key" and skip it.
 */
export function parseBindingKey(
  key: string,
): { elementId: string; eventType: string } | null {
  const colonIdx = key.lastIndexOf(":");
  if (colonIdx <= 0) return null;
  return {
    elementId: key.slice(0, colonIdx),
    eventType: key.slice(colonIdx + 1),
  };
}

/**
 * Normalize one inline-event value to an {@link EventBinding}, preserving the
 * studio/page-editor `collectEvents` semantics EXACTLY:
 *
 * - a string shorthand becomes a NEW object `{ flow: <string> }`
 * - an object is returned BY REFERENCE, unchanged (no copy, no added `__ns`)
 *
 * Never sets `__ns` — that is added later at registration time, and a
 * behavior-preserving extraction must not start emitting it.
 */
export function normalizeEventBinding(value: string | EventBinding): EventBinding {
  return typeof value === "string" ? { flow: value } : value;
}
